import React, { ReactNode, useEffect } from "react";
import { GrowlMessage } from "@campgladiator/cgui-core.molecules.growl-message";
import { Role, SubscriptionDTO } from "@campgladiator/cg-common.types.types";
import { ButtonProps } from "@campgladiator/cgui-core.atoms.button";
import { UpgradeMembershipModal, CancelMembershipModal } from "./components";
import {
  useMembershipManagementCancel,
  useMembershipManagementCreate,
  useMembershipManagementUpgrade,
  useUpgradeMembership,
} from "./hooks";
import { CreateMembershipModal } from "./components/CreateMembershipModal";

export type MembershipManagementProps = {
  /**
   * Event that fires when the modal is manually closed by the user.
   */
  onClose?: (success?: boolean) => void;
  /**
   * The user role that is accessing this functionality as we have different behavior regarding to this.
   */
  role: Role;
  /*
   * The theme of the button
   */
  theme?: ButtonProps["theme"];
  /**
   * The logged in user token
   */
  token: string;
};

export type MembershipManagementForExistingProps = {
  /**
   * The subscription object of the subscription to be updated
   * */
  subscription: SubscriptionDTO;
};

export type MembershipManagementCreateProps = {
  /**
   * The user id for creating a new subscription
   */
  userId: string;

  /**
   * The stripe customer id for creating a new subscription
   */
  stripeCustomerId: string;

  /**
   * The selling trainer id for creating a new subscription
   */
  sellingTrainerId: string;
} & MembershipManagementProps;

export type MembershipManagementUpgradeProps = {
  /**
   * Feature flag to hide/show the coupon step
   */
  isCouponStepVisible?: boolean;
} & MembershipManagementProps &
  MembershipManagementForExistingProps;

export type MembershipManagementCancelProps = {} & MembershipManagementProps &
  MembershipManagementForExistingProps;

const MembershipManagement = ({ children }: { children: ReactNode }) => ({
  children,
});

const Create = ({
  onClose,
  userId,
  role,
  token,
  stripeCustomerId,
  sellingTrainerId,
  theme = "trainer",
}: MembershipManagementCreateProps) => {
  const { membershipManagementCreateModalProps } =
    useMembershipManagementCreate({
      onClose,
      userId,
      stripeCustomerId,
      sellingTrainerId,
      role,
      token,
      theme,
    });

  return <CreateMembershipModal {...membershipManagementCreateModalProps} />;
};

const Cancel = ({
  onClose,
  subscription,
  role,
  token,
  theme = "trainer",
}: MembershipManagementCancelProps) => {
  const { membershipManagementCancelModalProps } =
    useMembershipManagementCancel({
      onClose,
      subscription,
      role,
      token,
      theme,
    });

  return <CancelMembershipModal {...membershipManagementCancelModalProps} />;
};

const Upgrade = ({
  onClose = (success?: boolean) => {},
  subscription,
  isCouponStepVisible,
  role,
  token,
  theme = "trainer",
}: MembershipManagementUpgradeProps) => {
  const {
    areEligibleProductsLoading,
    genericErrorMessage,
    growlMessageDescription,
    setGrowlMessageDescription,
    growlMessageVisible,
    setGrowlMessageVisible,
    productDetails,
    setUsersUpgradeEligibleProductDetails,
  } = useMembershipManagementUpgrade();

  const { upgradeMembershipProps } = useUpgradeMembership({
    areEligibleProductsLoading,
    genericErrorMessage,
    productDetails,
    subscription,
    onClose: onClose,
    setGrowlMessageDescription,
    setGrowlMessageVisible,
    isCouponStepVisible,
    role,
    token,
    theme,
  });

  const { upgradeType } = upgradeMembershipProps;

  useEffect(() => {
    setUsersUpgradeEligibleProductDetails([subscription], upgradeType);
  }, [subscription, upgradeType]);

  return (
    <>
      <GrowlMessage
        autoClose={true}
        isVisible={growlMessageVisible}
        onClick={() => setGrowlMessageVisible(false)}
      >
        {growlMessageDescription}
      </GrowlMessage>
      <UpgradeMembershipModal {...upgradeMembershipProps} />
    </>
  );
};

export default { MembershipManagement, Upgrade, Cancel, Create };
